<template>
  <div
    id="icon-code-snippet"
    class="icon-code-snippet codeblock-tabbed margin-bottom-xl"
  >
    <div v-if="icon">
      <div
        class="tabs"
        role="tablist"
      >
        <button
          v-for="syntax in syntaxes"
          :id="`tab-${sluggifySyntax(syntax)}`"
          :key="syntax"
          class="tab size-sm"
          style="--tab-color: var(--fa-blue); letter-spacing: 1px"
          type="button"
          role="tab"
          :aria-controls="`code-snippet-${sluggifySyntax(syntax)}`"
          :aria-selected="showing == sluggifySyntax(syntax) ? 'true' : 'false'"
          @click="showSnippet(sluggifySyntax(syntax))"
        >
          {{ syntax }}
        </button>
        <button
          v-if="!isHidingSvg"
          id="tab-svg"
          class="tab size-sm"
          style="--tab-color: var(--fa-blue); letter-spacing: 1px"
          type="button"
          role="tab"
          aria-controls="code-snippet-svg"
          :aria-selected="showing == 'svg' ? 'true' : 'false'"
          @click="showSnippet(sluggifySyntax('svg'))"
        >
          SVG
        </button>
      </div>
    </div>

    <div>
      <div class="code-snippet-content codeblock-container">
        <icon-code-snippet-html
          :default-styles="defaultStyles"
          :has-custom-styling="hasCustomStyling"
          :icon="icon"
          :is-duotone="isDuotone"
          :showing="showing"
          :styling="styling"
          :translated-style="translatedStyle"
          :version="version"
          @markCopied="markCopied"
        />

        <icon-code-snippet-react
          :default-styles="defaultStyles"
          :has-custom-styling="hasCustomStyling"
          :icon="icon"
          :showing="showing"
          :styling="styling"
          :translated-style="translatedStyle"
          :version="version"
          @markCopied="markCopied"
        />

        <icon-code-snippet-vue
          :default-styles="defaultStyles"
          :has-custom-styling="hasCustomStyling"
          :icon="icon"
          :is-custom-icon="isCustomIcon"
          :showing="showing"
          :styling="styling"
          :translated-style="translatedStyle"
          :version="version"
          @markCopied="markCopied"
        />

        <icon-code-snippet-svg
          :active-version="activeVersion"
          :default-styles="defaultStyles"
          :icon="icon"
          :icon-abstract="iconAbstract"
          :has-custom-styling="hasCustomStyling"
          :showing="showing"
          :styling="styling"
          :version="version"
          :viggo="viggo"
          @copySvg="copySvg"
        />
      </div>
    </div>
  </div>
</template>

<script>
  const JUST_COPIED_TIMEOUT = 1000

  import IconCodeSnippetHtml from './IconCodeSnippetHtml'
  import IconCodeSnippetReact from './IconCodeSnippetReact'
  import IconCodeSnippetSvg from './IconCodeSnippetSvg'
  import IconCodeSnippetVue from './IconCodeSnippetVue'
  import isEqual from 'lodash/isEqual'
  import sluggify from 'app/utils/sluggify'
  import { EventTrackingMixin } from 'app/mixins'

  export default {
    name: 'IconCodeSnippet',

    components: {
      IconCodeSnippetHtml,
      IconCodeSnippetReact,
      IconCodeSnippetSvg,
      IconCodeSnippetVue
    },

    mixins: [EventTrackingMixin],

    inject: ['currentVersion'],

    props: {
      canDownloadIcon: {
        type: Boolean,
        required: true
      },
      defaultStyles: {
        type: Object
      },
      icon: {
        type: Object
      },
      iconAbstract: {
        type: Object
      },
      isCustomIcon: {
        type: Boolean
      },
      isDuotone: {
        type: Boolean
      },
      isProUser: {
        type: Boolean,
        required: true
      },
      isProIcon: {
        type: Boolean,
        required: true
      },
      isUsingModal: {
        type: Boolean,
        required: true
      },
      kitVersion: {
        type: Number
      },
      showing: {
        type: String
      },
      styling: {
        type: Object
      },
      isHidingSvg: {
        type: Boolean,
        default: false
      },
      viggo: {
        validator(value) {
          return !!value && !!value.SVG
        }
      }
    },

    data() {
      return {
        copyIcon: 'fas fa-copy',
        svgSource: '',
        justCopied: ''
      }
    },

    computed: {
      activeVersion() {
        return `v${this.$releases.active.version}`
      },

      hasCustomStyling() {
        return !isEqual(this.defaultStyles, this.styling)
      },

      id() {
        return this.icon?.name
      },

      syntaxes() {
        return ['HTML', 'REACT', 'VUE']
      },

      translatedStyle() {
        if (this.version === 5) {
          switch (this.styling.family) {
            case 'brands':
              return 'fab'
            case 'kit':
              return 'fak'
            default:
              return `fa${this.styling.style.charAt(0)}`
          }
        } else {
          switch (this.styling.family) {
            case 'classic':
              return `fa-${this.styling.style}`

            case 'brands':
            case 'kit':
            case 'kit-duotone':
              return `fa-${this.styling.family}`

            default:
              return `fa-${this.styling.family} fa-${this.styling.style}`
          }
        }
      },

      version() {
        return this.kitVersion ? this.kitVersion : this.currentVersion.number
      }
    },

    methods: {
      markCopied(what) {
        const oldCopyIcon = this.copyIcon

        this.copyIcon = 'fas fa-check'

        this.justCopied = what
        setTimeout(() => {
          this.copyIcon = oldCopyIcon
          this.justCopied = null
        }, JUST_COPIED_TIMEOUT)

        this.$posthog.capture(`icon_code_snippet_${what}_copy_click`, {
          context: `${this.iconModalContext(this.isUsingModal)}`,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })
      },

      showSnippet(snippet) {
        this.$emit('show', snippet)
        this.$posthog.capture(`icon_code_snippet_${snippet}_view`, {
          context: `${this.iconModalContext(this.isUsingModal)}`,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })
      },

      sluggifySyntax(syntax) {
        return sluggify(syntax)
      },

      async copySvg() {
        this.$emit('getSelectedAction', 'Downloading this Icon')

        if (this.canDownloadIcon) {
          this.$clipboard(this.svgSource)
          this.markCopied('svg')
        } else {
          this.$emit('openProIconModal')
          this.purchaseFlowEvent('icon_code_snippet_svg_copy_click', {
            context: `${this.iconModalContext(this.isUsingModal)}`,
            icon_name: this.icon.name,
            icon_style: this.styling.style,
            icon_family: this.styling.family
          })
        }
      }
    }
  }
</script>
